import moment from 'moment'
import { EntityParams } from 'app/models/entity-params'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { FundGroup, FundGroupVisibility } from 'app/models/fund-group'
import { DROPDOWN_SEARCH_THRESHOLD, FUND_GROUP_COPY } from './fund-group-copy'

export type EntitiesById = Map<string, EntityParams>

export const indexEntities = (entities: EntityParams[]): EntitiesById =>
  new Map(entities.filter(e => !!e.id).map(e => [e.id as string, e]))

/** How `Entity` records its manager: display text (the manager's name) or the manager's MEI. */
export const isManagedBy = (
  manager: EntityParams,
  entity: EntityParams,
): boolean =>
  entity.id !== manager.id &&
  ((!!entity.fundManager && entity.fundManager === manager.entityName) ||
    (!!manager.mei &&
      !!entity.fundManagerMei &&
      entity.fundManagerMei === manager.mei))

export const entitiesManagedBy = (
  manager: EntityParams | undefined,
  entities: EntityParams[],
): EntityParams[] =>
  manager
    ? entities
        .filter(e => isManagedBy(manager, e))
        .sort((a, b) => a.entityName.localeCompare(b.entityName))
    : []

/**
 * The fund manager whose groups apply to this trade: the counterparty on OUR side of the trade
 * (`myEntity`), falling back to the other side when only that one manages entities. A trade
 * with no managing counterparty has no quick allocations.
 */
export const resolveTradeFundManager = (
  row: Pick<MasterTradeParams, 'myEntity' | 'buyerId' | 'sellerId'>,
  entities: EntityParams[],
): EntityParams | undefined => {
  const mine = row.myEntity === 'Buy' ? row.buyerId?.id : row.sellerId?.id
  const theirs = row.myEntity === 'Buy' ? row.sellerId?.id : row.buyerId?.id
  const candidates = [mine, theirs]
    .filter((id): id is string => !!id)
    .map(id => entities.find(e => e.id === id))
    .filter((e): e is EntityParams => !!e)
  return (
    candidates.find(manager => entities.some(e => isManagedBy(manager, e))) ??
    candidates[0]
  )
}

const normalize = (s: string) => s.trim().toLowerCase()

export const entityNamesOf = (
  group: FundGroup,
  entitiesById: EntitiesById,
): string[] => group.entityIds.map(id => entitiesById.get(id)?.entityName ?? id)

/** Manage-view and dropdown search: by group name OR any member entity's name. */
export const filterGroups = (
  groups: FundGroup[],
  query: string,
  entitiesById: EntitiesById,
): FundGroup[] => {
  const q = normalize(query)
  if (!q) return groups
  return groups.filter(
    g =>
      normalize(g.name).includes(q) ||
      entityNamesOf(g, entitiesById).some(name => normalize(name).includes(q)),
  )
}

/** The members not yet on the table, resolved to entities that may be allocated on this trade. */
export const entitiesToAppend = (
  group: FundGroup,
  currentEntityIds: Set<string>,
  candidates: EntityParams[],
): EntityParams[] => {
  const byId = indexEntities(candidates)
  return group.entityIds
    .filter(id => !currentEntityIds.has(id))
    .map(id => byId.get(id))
    .filter((e): e is EntityParams => !!e)
}

/**
 * For the edit form: the group's CURRENT members first (so a wrong one is easy to spot and
 * untick), then everything else. Both halves keep their incoming order. The pinned set is the
 * group as opened, not the live selection, so rows do not jump while the user toggles.
 */
export const orderEntitiesPinnedFirst = (
  entities: EntityParams[],
  pinnedIds: readonly string[],
): EntityParams[] => {
  if (pinnedIds.length === 0) return entities
  const pinned = new Set(pinnedIds)
  return [
    ...entities.filter(e => pinned.has(e.id ?? '')),
    ...entities.filter(e => !pinned.has(e.id ?? '')),
  ]
}

export const shouldShowDropdownSearch = (groupCount: number): boolean =>
  groupCount > DROPDOWN_SEARCH_THRESHOLD

export interface FundGroupFormValues {
  name: string
  visibility: FundGroupVisibility
  entityIds: string[]
}

export interface FundGroupFormErrors {
  name?: string
  entityIds?: string
}

export const validateGroupForm = (
  values: FundGroupFormValues,
  existingGroups: Pick<FundGroup, 'id' | 'name'>[],
  editingId?: string,
): FundGroupFormErrors => {
  const errors: FundGroupFormErrors = {}
  const name = values.name.trim()
  if (!name) {
    errors.name = FUND_GROUP_COPY.nameRequired
  } else if (
    existingGroups.some(
      g => g.id !== editingId && normalize(g.name) === normalize(name),
    )
  ) {
    errors.name = FUND_GROUP_COPY.nameDuplicate
  }
  if (values.entityIds.length === 0) {
    errors.entityIds = FUND_GROUP_COPY.entitiesRequired
  }
  return errors
}

/** "Private · 2 entities: LendOS Fund 1, LendOS Fund 2" — the dropdown item's second line. */
export const describeGroup = (
  group: FundGroup,
  entitiesById: EntitiesById,
): string => {
  const names = entityNamesOf(group, entitiesById)
  return `${group.visibility} · ${FUND_GROUP_COPY.entityCount(names.length)}: ${names.join(', ')}`
}

export const formatCreated = (iso: string): string =>
  moment(iso).format('MMM D, YYYY')
